import { useEffect, useState } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Divider, Stack } from "@mui/material";
import DamsTable from "../../components/Table/CollapsibleTable";

import { TRowItem } from "../../components/Table/TableRow";
import { assetApi } from "../../services/assets/asset-crud";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import {
  IAssetRecord,
  ICreateAssetDistribution,
  IPagination,
  IQueryPerams,
} from "../../config/types";

import { useDmsForm } from "../../hooks/useDmsFrom";
import { usePagination } from "../../hooks/usePagination";
import { DomainSelectInput } from "../../common-ui/select-input-models/DomainSelectInput";
import { Field } from "../../utils/formSchema";
import { dmsDomainsNamesTy } from "../../config/constants/domain";
import { assetBatchDistributionApi } from "../../services/distribution/asset-distribution";

import { formatDate } from "../../utils/common";
import { AssetLogsFilter } from "../../common-ui/page-filters";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import useAppNavigation from "../../hooks/useAppNavigation";
import appRoutePath from "../../config/path";

function NewAssetDistributionPage() {
  const [dataset, setDataset] = useState<TRowItem[]>([]);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(assetApi.getAssets);

  const { form, updateForm, setSubmitAttempt, isFormValid, getFormValues } =
    useDmsForm<ICreateAssetDistribution>({
      from_location_id: "",
      from_location_domain: "",
      to_location_domain: "",
      to_location_id: "",
      assets: [],
    });

  const { selectedRowItems, toggleRow, toggleSelectAllRow } =
    useMangeTableItems(dataset);
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });
  const populateDataSet = (data: IAssetRecord[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      date_received: formatDate(data.date_received),
      asset_meta_raw: data.asset_meta
        ? data.asset_meta.map(
            (item) => ` ${item.asset_tag}=${item.meta_value} `
          )
        : [],
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };
  const fetchData = async () => {
    const result = await assetApi.unallocated();
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    updateForm((prev) => ({
      ...prev,
      assets: {
        value: selectedRowItems.map((row) => row.id),
        isValid: selectedRowItems.length > 0,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowItems.length]);

  const navigate = useAppNavigation();
  function cancelAddItem() {
    alertDialogStore.open({
      title: "Unsaved Changes",
      subtitle: "Do you want todiscard changed and go back to logs page?",
      onRightBtnPress: () =>
        navigate({ path: appRoutePath.OUTBOUND_ASSET_ASSIGNMENT_PAGE }),
      isSuccess: false,
    });
  }

  function handleToDomainSelect(item: Field, domain: dmsDomainsNamesTy) {
    updateForm((p) => ({
      ...p,
      to_location_id: item,
      to_location_domain: { value: domain, isValid: true },
    }));
  }

  function handleFromDomainSelect(item: Field, domain: dmsDomainsNamesTy) {
    updateForm((p) => ({
      ...p,
      from_location_id: item,
      from_location_domain: { value: domain, isValid: true },
    }));
  }

  async function onSubmit() {
    setSubmitAttempt(true);
    if (isFormValid()) {
      const data = getFormValues();
      const result = await assetBatchDistributionApi.createBatchDistribution(
        data
      );
      alertDialogStore.open({
        title: "Create New Asset Distribution",
        subtitle: result.message,
        onLeftBtnPress: result.success ? onSeeAllPress : () => {},
        leftBtnTxt: result.success ? "See All" : "Canel",
        isSuccess: result.success,
      });
    }
  }


  async function handleSubmit() {
    const isFieldValid = isFormValid([], ["assets"]);
  
    if (isFieldValid && !form.assets.isValid) {
      alertDialogStore.open({
        title: "Asset Selection is Required",
        subtitle: "Please select at least one Asset from the table below to continue",
        onLeftBtnPress: () => {},
        isSuccess: false,
        rightBtnTxt: "Okay",
        leftBtnTxt: "Close",
      });
      return; 
    }
  
    const isFormCompletelyValid = isFormValid(); 
  
    if (!isFormCompletelyValid) {
      alertDialogStore.open({
        title: "Create New Asset Distribution",
        subtitle: "Please fill all required fields",
        onLeftBtnPress: () => {},
        isSuccess: false,
        rightBtnTxt: "Okay",
        leftBtnTxt: "Close",
      });
      return; 
    }
  

    actionDialogStore.open({
      title: "Create New Asset Distribution",
      subtitle: dialogMessage(selectedRowItems),
      onRightBtnPress: onSubmit,
    });
  }
  

  function onSeeAllPress() {
    navigate({ path: appRoutePath.OUTBOUND_ASSET_DISTRIBUTIONS_PAGE });
  }

  return (
    <MainPageBase
      pageState={'main'}
      title="New Asset Distribution"
      subtitle="Select the source and destination domains, choose assets to distribute, and initiate the transfer process."
      addItemBtnOnClick={handleSubmit}
      addItemBtnText={"Create Distribution"}
      cancelBtnClick={() => cancelAddItem()}
    >
      <Box
        height={`100%`}
        display={"flex"}
        justifyContent={"center"}
      >
        <Stack width={"100%"}>
          <Box mb={3} display={"flex"} justifyContent={"space-between"}>
            <Stack direction={"row"} display={"flex"} spacing={5}>
              <Box width={320}>
                <DomainSelectInput
                  direction="column"
                  label="Distributor  Domain Name"
                  placeholder="Select Distributor  Domain Name"
                  handleChange={handleFromDomainSelect}
                />
              </Box>
              <Divider orientation="vertical" />
              <Box width={320}>
                <DomainSelectInput
                  direction="column"
                  placeholder="Select Recipent Domain Name"
                  label="Recipent Domain Name"
                  handleChange={handleToDomainSelect}
                />
              </Box>
            </Stack>
          </Box>

          <AssetLogsFilter<IAssetRecord[]>
            ftn={assetApi.getAssets}
            updateDataSet={populateDataSet}
            updateQuerySet={setQuerySet}
            isAssigned={false}
            isDeleted={false}
          />

          <DamsTable
            tableTitle="Available Assets for Distribution"
            tableSubtitle="Select assets from the list below to include them in the new distribution batch."
            tableTitleBadge={
              selectedRowItems.length === 0
                ? ""
                : ` ${selectedRowItems.length} Assets selected`
            }
            tableHeaders={columns}
            data={dataset}
            tableKeys={tkeys}
            useTableCheckbox={true}
            selectedRowItems={selectedRowItems}
            onTableItemSelect={toggleRow}
            toggleSelectAllRow={toggleSelectAllRow}
            hasPagination
            paginationState={pagination}
            onNextPage={paginateToNextPage(querySet, populateDataSet)}
            onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
            setActivePage={paginateToPageNumber(populateDataSet, querySet)}
          />
        </Stack>
      </Box>
    </MainPageBase>
  );
}

const tkeys = [
  "asset_category_name",
  "serial_number",
  "brand",
  "embossment_number",
  "assigned",
  "warranty_in_months",
];

const columns = [
  "Asset Category",
  "Serial Number",
  "Brand",
  "Embossment Number",
  "Assigned",
  "warranty (months)",
];

const dialogMessage = (selectedRowItems: TRowItem[]) => {
  return `You are about to make a batch distribution for ${selectedRowItems.length} assets. Click 'Confirm' to continue.`;
};
export { NewAssetDistributionPage };
